import * as Sentry from '@sentry/react'

declare global {
  interface Window {
    Sentry?: typeof Sentry
  }
}

declare const __APP_VERSION__: string

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined
type BeforeSend = NonNullable<Parameters<typeof Sentry.init>[0]['beforeSend']>
type SentryEvent = Parameters<BeforeSend>[0]

const URL_FIELDS = /^(?:url|from|to|referer|referrer|description|transaction)$/i
const SECRET_FIELDS = /^(?:authorization|cookie|set-cookie)$/i

function stripQueryAndFragment(value: string): string {
  if (value.startsWith('?') || value.startsWith('#')) return ''

  try {
    const parsed = new URL(value, 'https://telemetry.invalid')
    if (/^https?:\/\//i.test(value)) {
      return `${parsed.origin}${parsed.pathname}`
    }
    if (value.startsWith('/')) return parsed.pathname
  } catch {
    // Fall through to token-wise URL redaction for description strings.
  }

  return value.replace(
    /https?:\/\/[^\s"'<>]+|\/[^\s"'<>?#]*[?#][^\s"'<>]*/gi,
    (candidate) => {
      try {
        const parsed = new URL(candidate, 'https://telemetry.invalid')
        return /^https?:\/\//i.test(candidate)
          ? `${parsed.origin}${parsed.pathname}`
          : parsed.pathname
      } catch {
        return candidate.split(/[?#]/, 1)[0]
      }
    },
  )
}

function sanitizeTelemetryObject(
  value: unknown,
  seen = new WeakSet<object>(),
): void {
  if (!value || typeof value !== 'object' || seen.has(value)) return
  seen.add(value)

  if (Array.isArray(value)) {
    for (const item of value) sanitizeTelemetryObject(item, seen)
    return
  }

  const record = value as Record<string, unknown>
  for (const [key, entry] of Object.entries(record)) {
    if (SECRET_FIELDS.test(key) || key === 'query_string') {
      delete record[key]
      continue
    }
    if (typeof entry === 'string' && URL_FIELDS.test(key)) {
      record[key] = stripQueryAndFragment(entry)
      continue
    }
    sanitizeTelemetryObject(entry, seen)
  }
}

export function sanitizeSentryEvent(event: SentryEvent): SentryEvent {
  if (event.request) delete event.request.data
  sanitizeTelemetryObject(event)
  return event
}

if (dsn) {
  Sentry.init({
    dsn,
    tunnel: '/api/monitoring',
    sendDefaultPii: false,
    tracesSampleRate: 0,
    maxBreadcrumbs: 50,
    release: __APP_VERSION__,
    beforeSend: sanitizeSentryEvent,
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'AbortError',
      'Failed to fetch',
      'NetworkError when attempting to fetch resource',
      'Load failed',
      'cancelled',
    ],
  })
  window.Sentry = Sentry
}

export { Sentry }
